import { useState, useEffect } from 'react';
import Modal, { ModalBody, ModalFooter } from './ui/Modal';
import Button from './ui/Button';
import TextArea from './ui/TextArea';

const REASONS = ['harassment', 'spam', 'inappropriate', 'uncomfortable', 'other'];

/**
 * Shared report form (reason + optional details for "other").
 * Parent supplies onSubmit and handles persistence / toasts.
 */
export default function ReportUserModal({ open, onClose, onSubmit, title = 'Report User' }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason('');
      setDetails('');
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!reason || (reason === 'other' && !details.trim())) return;
    setSubmitting(true);
    try {
      await onSubmit({ reason, details: details.trim() || null });
    } finally {
      setSubmitting(false);
    }
  };

  const disableSubmit = !reason || (reason === 'other' && !details.trim());

  return (
    <Modal isOpen={open} onClose={onClose} title={title} size="sm">
      <ModalBody>
        <p className="mb-4 text-gray-600 dark:text-dark-muted">
          Please select a reason for reporting this user:
        </p>
        <div className="mb-4 flex flex-col gap-2">
          {REASONS.map((r) => (
            <Button
              key={r}
              type="button"
              fullWidth
              variant="ghost"
              onClick={() => setReason(r)}
              className={`!h-auto min-h-10 justify-start px-4 py-3 text-left font-normal capitalize focus-visible:ring-offset-modal-bg dark:focus-visible:ring-offset-modal-bg ${
                reason === r
                  ? 'border-2 border-primary bg-primary/10 font-medium text-gray-900 dark:text-ink hover:bg-primary/15'
                  : 'border border-gray-200 dark:border-dark-border text-gray-700 dark:text-dark-text hover:bg-gray-500/15 dark:hover:bg-white/10'
              }`}
            >
              {r}
            </Button>
          ))}
        </div>
        {reason === 'other' && (
          <TextArea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Please describe the issue..."
            className="mb-4"
            rows={3}
          />
        )}
      </ModalBody>
      <ModalFooter align="split">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={handleSubmit}
          disabled={disableSubmit}
          loading={submitting}
        >
          Submit Report
        </Button>
      </ModalFooter>
    </Modal>
  );
}
